import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useClientAuth } from "@/context/ClientAuthContext";
import { usePageMeta, useNoIndex } from "@/lib/seo";
import { FileCheck, Plane, Palmtree, MessageSquare, LogOut, FileText, Wallet, Clock, MessageCircle, Lock } from "lucide-react";
import { waLink } from "@/lib/api";

const TYPE_META = {
  visa: { icon: FileCheck, label: "Visa Application" },
  flight: { icon: Plane, label: "Flight Enquiry" },
  holiday: { icon: Palmtree, label: "Holiday Request" },
  contact: { icon: MessageSquare, label: "General Enquiry" },
};

const statusColor = (s = "") => {
  const t = s.toLowerCase();
  if (t.includes("approved") || t.includes("complete") || t.includes("issued")) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (t.includes("reject") || t.includes("cancel")) return "bg-red-50 text-red-600 border-red-200";
  if (t.includes("new")) return "bg-[#F3F4F6] text-[#4B5563] border-[#E5E7EB]";
  return "bg-[#FDF8EC] text-[#BA901D] border-[#D4A62A]/30";
};

export default function ClientDashboard() {
  const { client, loading, logout } = useClientAuth();
  const [inquiries, setInquiries] = useState(null);
  usePageMeta("My Account", "Track your Makchester visa applications, enquiries and bookings.");
  useNoIndex();

  useEffect(() => {
    if (client) api.get("/client/my-inquiries").then((r) => setInquiries(r.data)).catch(() => setInquiries([]));
  }, [client]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-[#4B5563] text-sm">Loading your account…</div>;
  if (!client) return <Navigate to="/account/login" replace />;

  return (
    <div className="bg-[#F9FAFB] min-h-[70vh] py-16" data-testid="client-dashboard">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-10">
          <div className="flex items-center gap-4">
            {client.picture ? (
              <img src={client.picture} alt={client.name} className="w-14 h-14 rounded-full object-cover border-2 border-[#D4A62A]" />
            ) : (
              <div className="w-14 h-14 rounded-full bg-[#0A2E4D] text-[#E3BA45] flex items-center justify-center font-serif-display text-2xl">{client.name?.charAt(0)}</div>
            )}
            <div>
              <h1 className="font-serif-display text-2xl sm:text-3xl text-[#0A2E4D]" data-testid="client-welcome">Welcome, {client.name?.split(" ")[0]}</h1>
              <p className="text-xs text-[#4B5563] mt-0.5">{client.email} · {client.auth_provider === "google" ? "Google account" : "Email account"}</p>
            </div>
          </div>
          <button onClick={logout} className="inline-flex items-center gap-2 text-sm font-semibold text-[#4B5563] hover:text-red-500 transition-colors self-start" data-testid="client-logout-btn">
            <LogOut size={15} /> Sign out
          </button>
        </div>

        <div className="flex items-center justify-between mb-5">
          <h2 className="font-serif-display text-xl text-[#0A2E4D]">My Applications &amp; Enquiries</h2>
          <Link to="/visa" className="mk-btn-gold !px-5 !py-2.5 !text-xs" data-testid="client-new-application-btn">+ New Visa Application</Link>
        </div>

        {inquiries === null ? (
          <div className="text-sm text-[#4B5563]">Loading…</div>
        ) : inquiries.length === 0 ? (
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-12 text-center" data-testid="client-empty-state">
            <FileText size={36} className="text-[#D4A62A] mx-auto" strokeWidth={1.2} />
            <h3 className="font-serif-display text-lg text-[#0A2E4D] mt-4">Nothing here yet</h3>
            <p className="text-sm text-[#4B5563] mt-2 max-w-sm mx-auto">Submit a visa application, flight or holiday enquiry using <strong>{client.email}</strong> and it will appear here automatically.</p>
            <div className="flex flex-wrap justify-center gap-3 mt-6">
              <Link to="/visa" className="mk-btn-gold !px-5 !py-2.5 !text-xs">Apply for a Visa</Link>
              <Link to="/flights" className="mk-btn-navy !px-5 !py-2.5 !text-xs">Flight Enquiry</Link>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {inquiries.map((inq) => {
              const meta = TYPE_META[inq.service_type] || TYPE_META.contact;
              return (
                <div key={inq.id} className="bg-white border border-[#E5E7EB] rounded-2xl p-6" data-testid={`client-inquiry-${inq.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-xl bg-[#0A2E4D] flex items-center justify-center"><meta.icon size={18} className="text-[#E3BA45]" /></div>
                      <div>
                        <div className="font-semibold text-[#0A2E4D] text-sm">{meta.label}{inq.visa_type ? ` — ${inq.visa_type}` : inq.destination ? ` — ${inq.destination}` : ""}</div>
                        <div className="text-xs text-[#9CA3AF] mt-0.5 flex items-center gap-1.5"><Clock size={11} /> Submitted {new Date(inq.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</div>
                      </div>
                    </div>
                    <span className={`text-[11px] font-bold uppercase tracking-wider border rounded-full px-3 py-1.5 ${statusColor(inq.status)}`} data-testid={`inquiry-status-${inq.id}`}>{inq.status}</span>
                  </div>
                  {(inq.documents?.length > 0 || inq.payment) && (
                    <div className="mt-4 pt-4 border-t border-dashed border-[#E5E7EB] grid sm:grid-cols-2 gap-4">
                      {inq.documents?.length > 0 && (
                        <div>
                          <div className="text-[11px] uppercase tracking-wider font-bold text-[#4B5563] mb-2 flex items-center gap-1.5"><FileText size={11} /> Submitted Documents</div>
                          <ul className="space-y-1">
                            {inq.documents.map((d) => <li key={d.field} className="text-xs text-[#4B5563] flex items-center gap-1.5"><span className="text-[#D4A62A]">•</span>{d.filename} <span className="text-[#9CA3AF]">({d.field.replace(/_/g, " ")})</span></li>)}
                          </ul>
                        </div>
                      )}
                      {inq.payment && (
                        <div>
                          <div className="text-[11px] uppercase tracking-wider font-bold text-[#4B5563] mb-2 flex items-center gap-1.5"><Wallet size={11} /> Payment</div>
                          <div className="text-xs text-[#4B5563] space-y-1">
                            <div className="flex justify-between max-w-[200px]"><span>Total</span><span className="font-semibold text-[#0A2E4D]">${inq.payment.total.toLocaleString()}</span></div>
                            <div className="flex justify-between max-w-[200px]"><span>Paid</span><span className="font-semibold text-emerald-600">${inq.payment.paid.toLocaleString()}</span></div>
                            <div className="flex justify-between max-w-[200px]"><span>Balance</span><span className="font-semibold text-[#BA901D]">${inq.payment.balance.toLocaleString()}</span></div>
                          </div>
                          {inq.payment_due && (
                            <Link to={`/pay/${inq.payment_due.token}`} data-testid={`pay-now-${inq.id}`}
                              className="mt-3 inline-flex items-center gap-1.5 bg-[#D4A62A] hover:bg-[#BA901D] text-white px-4 py-2 rounded-full text-[11px] font-bold uppercase tracking-wider transition-colors">
                              <Lock size={11} /> Pay Securely by Card — AED {inq.payment_due.amount_aed.toLocaleString()}
                            </Link>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-10 bg-[#0A2E4D] rounded-2xl p-8 text-center">
          <h3 className="font-serif-display text-xl text-white">Need an update on your application?</h3>
          <p className="text-slate-300 text-sm mt-2">Your consultant is one message away.</p>
          <a href={waLink(`Hello Makchester, I'm ${client.name} (${client.email}). I'd like an update on my application.`)} target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] text-white px-6 py-3 rounded-full text-sm font-semibold mt-5 transition-colors">
            <MessageCircle size={15} /> WhatsApp a Travel Expert
          </a>
        </div>
      </div>
    </div>
  );
}
